import React from 'react';
import { cn } from "@/lib/utils";
import { forwardRef } from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "outline" | "ghost" | "glass" | "neumorphic";
  size?: "default" | "sm" | "lg";
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "default",
      asChild: _asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = "button";
    return (
      <Comp
        className={cn(
          "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500",
          "disabled:pointer-events-none disabled:opacity-50",
          {
            "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95":
              variant === "default",
            "border-2 border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm hover:shadow-md transform hover:scale-105 active:scale-95 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 dark:hover:border-gray-500":
              variant === "outline",
            "text-gray-700 hover:bg-gray-100 hover:shadow-sm transform hover:scale-105 active:scale-95 dark:text-gray-200 dark:hover:bg-gray-700":
              variant === "ghost",
            "bg-[var(--glass-bg)] backdrop-blur-md border border-[var(--glass-border)] text-gray-700 hover:bg-[rgba(255,255,255,0.3)] shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 dark:text-gray-100 dark:hover:bg-[rgba(0,0,0,0.3)]":
              variant === "glass",
            "bg-[#e6e6e6] text-gray-700 shadow-[8px_8px_16px_#d1d9e6,_-8px_-8px_16px_#ffffff] hover:shadow-[12px_12px_24px_#c8d0e7,_-12px_-12px_24px_#ffffff] hover:transform hover:translate-y-[-2px] active:shadow-[inset_4px_4px_8px_#d1d9e6,_inset_-4px_-4px_8px_#ffffff] active:transform active:translate-y-[1px] dark:bg-gray-700 dark:text-gray-100 dark:shadow-[8px_8px_16px_#1a1a1a,_-8px_-8px_16px_#2a2a2a] dark:hover:shadow-[12px_12px_24px_#151515,_-12px_-12px_24px_#252525] dark:active:shadow-[inset_4px_4px_8px_#1a1a1a,_inset_-4px_-4px_8px_#2a2a2a]":
              variant === "neumorphic",
            "h-10 px-4 py-2": size === "default",
            "h-9 px-3 text-sm": size === "sm",
            "h-11 px-8": size === "lg",
          },
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button };
